/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AccessControlTab.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import customConsole from "../../../config/customConsole";
import { HandleApiActions, labCar } from "../../../redux/actions";
import {
  Box,
} from "@mui/material";
import {
  EEDetails,
  ModuleAccessTypes,
  ProjectDetails,
  SelectModules,
  SWMDetails,
  VariantDetailsBox,
  VehicleComponentDetails,
  VehicleSystemDetails,
} from "../../../components";

import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import AccessOverviewDrawer from "../ProjectAccessDrawer";

function AccessControlTab({
  selectedProjectDetails,
  projectStatus,
  moduleTypes,
  accessRequestStatus,
}) {
  const dispatch = useDispatch();

  const [selectedPaBtn, setSelectedPaBtn] = useState(moduleTypes?.PROJECT);


  const [variantDetails, setVariantDetails] = useState([]);
  const [variantOptionsList, setVariantOptionsList] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedVariantDetails, setSelectedVariantDetails] = useState(null);

  const [zoneOptionsList, setZoneOptionsList] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedZoneDetails, setSelectedZoneDetails] = useState(null);

  const [zoneEEDetails, setZoneEEDetails] = useState([]);
  const [eeOptionsList, setEEOptionsList] = useState([]);
  const [selectedEE, setSelectedEE] = useState(null);
  const [selectedEEDetails, setSelectedEEDetails] = useState(null);

  const [eeSwmDetails, setEESwmDetails] = useState([]);
  const [swmOptionsList, setSWMOptionsList] = useState([]);
  const [selectedSWM, setSelectedSWM] = useState(null);
  const [selectedSWMDetails, setSelectedSWMDetails] = useState(null);

  const [vehicleSystemDetails, setVehicleSystemDetails] = useState([]);
  const [vehicleSystemOptionsList, setVehicleSystemOptionsList] = useState([]);
  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState(null);
  const [selectedVehicleSystemDetails, setSelectedVehicleSystemDetails] =
    useState(null);

  const [vehicleComponentDetails, setVehicleComponentDetails] = useState([]);
  const [vehicleComponentOptionsList, setVehicleComponentOptionsList] =
    useState([]);
  const [selectedVehicleComponent, setSelectedVehicleComponent] =
    useState(null);
  const [selectedVehicleComponentDetails, setSelectedVehicleComponentDetails] =
    useState(null);

  const [showOverview, setShowOverview] = useState(false);
  const [accessControlData, setAccessControlData] = useState({});

  useEffect(() => {
    console.log("selectedPaBtn:", selectedPaBtn);
    console.log("moduleTypes:", moduleTypes);
  }, [selectedPaBtn, moduleTypes]);

  const handleAccessBtnClick = (params) => {
    console.log("--handleAccessBtnClick--", params);
    customConsole("handleAccessBtnClick: ");
    customConsole(params);

    setAccessControlData(params);
    setShowOverview(true);
  };

  const getVariantDetails = ({ projectId }) => {
    let apiDetails = labCar.get_variant_details;

    customConsole({
      ...apiDetails,
      params: {
        project_id: projectId,
        is_complete_details_required: false,
      },
    });

    dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: projectId,
          is_complete_details_required: true,
        },
      }),
    ).then((resp) => {
      if (resp?.error) {
        setVariantDetails([]);
      } else {
        setVariantDetails(resp?.data);
      }
    });
  };

  const getZoneEEDetails = ({ projectId, variantId, zonePosition }) => {
    let apiDetails = labCar.get_ee_components_pa;

    customConsole({
      ...apiDetails,
      params: {
        project_id: projectId,
        variant_id: variantId,
        ...(zonePosition && { zone_position: zonePosition }),
        status: true,
      },
    });

    dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: projectId,
          variant_id: variantId,
          ...(zonePosition && { zone_position: zonePosition }),
          status: true,
        },
      }),
    ).then((resp) => {
      if (resp?.error) {
        setZoneEEDetails([]);
      } else {
        setZoneEEDetails(resp?.data);
      }
    });
  };

  const getEESwmDetails = ({ projectId, variantId, eeId }) => {
    let apiDetails = labCar.get_sw_modules_pa;

    customConsole({
      ...apiDetails,
      params: {
        project_id: projectId,
        variant_id: variantId,
        ee_id: eeId,
        status: true,
      },
    });

    dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: projectId,
          variant_id: variantId,
          ee_id: eeId,
          status: true,
        },
      }),
    ).then((resp) => {
      if (resp?.error) {
        setEESwmDetails([]);
      } else {
        setEESwmDetails(resp?.data);
      }
    });
  };

  const getVehicleSystemsDetails = ({
    projectId,
    variantId,
    zonePosition,
  }) => {
    const apiDetails = labCar.get_vehicle_systems_all;
    dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          ...(zonePosition && { zone_position: zonePosition }), // Add zone position if zonal
          is_for_req_access: true,
        },
      }),
    ).then((resp) => {
      if (resp?.error) {
        setVehicleSystemDetails([]);
      } else {
        setVehicleSystemDetails(resp?.data);
      }
    });
  };
  const getVehicleComponentsDetails = ({
    projectId,
    variantId,
    vsId,
  }) => {
    const apiDetails = labCar.get_vehicle_components_all;
    dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          project_id: projectId,
          variant_id: variantId,
          vs_id: vsId,
          is_for_req_access: true,
        },
      }),
    ).then((resp) => {
      if (resp?.error) {
        setVehicleComponentDetails([]);
      } else {
        setVehicleComponentDetails(resp?.data);
      }
    });
  };
  useEffect(() => {
    setSelectedPaBtn(moduleTypes?.PROJECT);
    setVariantOptionsList([]);
    setSelectedVariant(null);
    setSelectedVariantDetails(null);
    setZoneOptionsList([]);
    setSelectedZone(null);
    setSelectedZoneDetails(null);
    setEEOptionsList([]);
    setSelectedEE(null);
    setSelectedEEDetails(null);
    setSWMOptionsList([]);
    setSelectedSWM(null);
    setSelectedSWMDetails(null);
    setVehicleSystemOptionsList([]);
    setSelectedVehicleSystem(null);
    setSelectedVehicleSystemDetails(null);
    setVehicleComponentOptionsList([]);
    setSelectedVehicleComponent(null);
    setSelectedVehicleComponentDetails(null);
  }, [selectedProjectDetails]);

  useEffect(() => {
    setZoneOptionsList([]);
    setSelectedZone(null);
    setSelectedZoneDetails(null);
  }, [selectedVariant]);

  useEffect(() => {
    setEEOptionsList([]);
    setSelectedEE(null);
    setSelectedEEDetails(null);
    setVehicleSystemOptionsList([]);
    setSelectedVehicleSystem(null);
    setSelectedVehicleSystemDetails(null);
  }, [selectedVariant, selectedZone]);

  useEffect(() => {
    setSWMOptionsList([]);
    setSelectedSWM(null);
    setSelectedSWMDetails(null);
  }, [selectedVariant, selectedZone, selectedEE]);

  useEffect(() => {
    setVehicleComponentOptionsList([]);
    setSelectedVehicleComponent(null);
    setSelectedVehicleComponentDetails(null);
  }, [selectedVariant, selectedZone, selectedVehicleSystem]);

  useEffect(() => {
    if (selectedProjectDetails?.project_id) {
      getVariantDetails({
        projectId: selectedProjectDetails?.project_id,
      });
    }
  }, [selectedProjectDetails]);

  useEffect(() => {
    let filteredVariantOptionsList = variantDetails
      ?.map((el) => {
        return {
          value: el?.variant_id,
          label: el?.name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setVariantOptionsList(filteredVariantOptionsList);

    if (filteredVariantOptionsList?.length) {
      setSelectedVariant(filteredVariantOptionsList?.[0]?.value);
    }
  }, [variantDetails]);

  useEffect(() => {
    let filteredVariantDetails = variantDetails?.find(
      (el) => Number.parseInt(el?.variant_id) === Number.parseInt(selectedVariant),
    );
    setSelectedVariantDetails(filteredVariantDetails);
  }, [selectedVariant]);

  useEffect(() => {

    if (
      selectedProjectDetails?.project_id &&
      !selectedProjectDetails?.is_zonal &&
      selectedVariantDetails?.variant_id
    ) {
      getZoneEEDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
      });
      getVehicleSystemsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
      });
    }

    let filteredZoneOptionsList = selectedVariantDetails?.zone_details
      ?.map((el) => {
        return {
          value: el?.zone?.position,
          label: el?.zone?.zone_name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setZoneOptionsList(filteredZoneOptionsList);
    if (filteredZoneOptionsList?.length) {
      setSelectedZone(filteredZoneOptionsList?.[0]?.value);
    }
  }, [selectedVariantDetails]);

  useEffect(() => {
    let filteredSelectedZoneDetails =
      selectedVariantDetails?.zone_details?.find(
        (el) => el?.zone?.position === selectedZone,
      );
    setSelectedZoneDetails(filteredSelectedZoneDetails);

    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedZone
    ) {
      getZoneEEDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        zonePosition: selectedZone,
      });
      getVehicleSystemsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        zonePosition: selectedZone,
      });
    }
  }, [selectedZone]);

  useEffect(() => {
    let filteredEEOptionsList = zoneEEDetails
      ?.map((el) => {
        return {
          value: el?.ee_id,
          label: el?.description,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setEEOptionsList(filteredEEOptionsList);

    if (filteredEEOptionsList?.length) {
      setSelectedEE(filteredEEOptionsList[0]?.value);
    }
  }, [zoneEEDetails]);

  useEffect(() => {
    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedEE
    ) {
      getEESwmDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        eeId: selectedEE,
      });
    }

    let filteredSelectedEEDetails = zoneEEDetails?.find(
      (el) => Number.parseInt(el?.ee_id) === Number.parseInt(selectedEE),
    );
    setSelectedEEDetails(filteredSelectedEEDetails);
  }, [selectedEE]);

  useEffect(() => {
    let filteredSWMOptionsList = eeSwmDetails
      ?.map((el) => {
        return {
          value: el?.swm_id,
          label: el?.name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setSWMOptionsList(filteredSWMOptionsList);

    if (filteredSWMOptionsList?.length) {
      setSelectedSWM(filteredSWMOptionsList[0]?.value);
    }
  }, [eeSwmDetails]);

  useEffect(() => {
    let filteredEESwmDetails = eeSwmDetails?.find(
      (el) => Number.parseInt(el?.swm_id) === Number.parseInt(selectedSWM),
    );
    setSelectedSWMDetails(filteredEESwmDetails);
  }, [selectedSWM]);

  useEffect(() => {
    let filteredVehicleSystemOptionsList = vehicleSystemDetails
      ?.map((el) => {
        return {
          value: el?.id,
          label: el?.assembly_number,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setVehicleSystemOptionsList(filteredVehicleSystemOptionsList);

    console.log(
      "filteredVehicleSystemOptionsListfilteredVehicleSystemOptionsListfilteredVehicleSystemOptionsListfilteredVehicleSystemOptionsListfilteredVehicleSystemOptionsList",
      filteredVehicleSystemOptionsList,
    );
    if (filteredVehicleSystemOptionsList?.length) {
      setSelectedVehicleSystem(filteredVehicleSystemOptionsList[0]?.value);
    }
  }, [vehicleSystemDetails]);

  useEffect(() => {
    console.log(
      "selectedVehicleSystemselectedVehicleSystemselectedVehicleSystem",
      selectedVehicleSystem,
    );
    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedVehicleSystem
    ) {
      getVehicleComponentsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        vsId: selectedVehicleSystem,
      });
    }

    let filteredSelectedVehicleSystemDetails = vehicleSystemDetails?.find(
      (el) => Number.parseInt(el?.id) === Number.parseInt(selectedVehicleSystem),
    );
    console.log(
      "filteredSelectedVehicleSystemDetailsfilteredSelectedVehicleSystemDetailsfilteredSelectedVehicleSystemDetails",
      filteredSelectedVehicleSystemDetails,
    );
    setSelectedVehicleSystemDetails(filteredSelectedVehicleSystemDetails);
  }, [selectedVehicleSystem]);

  useEffect(() => {
    let filteredVehicleComponentOptionsList = vehicleComponentDetails
      ?.map((el) => {
        return {
          value: el?.id,
          label: el?.component_number,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setVehicleComponentOptionsList(filteredVehicleComponentOptionsList);

    if (filteredVehicleComponentOptionsList?.length) {
      setSelectedVehicleComponent(
        filteredVehicleComponentOptionsList[0]?.value,
      );
    }
  }, [vehicleComponentDetails]);

  useEffect(() => {
    let filteredVehicleComponentDetails = vehicleComponentDetails?.find(
      (el) => Number.parseInt(el?.id) === Number.parseInt(selectedVehicleComponent),
    );
    setSelectedVehicleComponentDetails(filteredVehicleComponentDetails);
  }, [selectedVehicleComponent]);

  return (
    <Box sx={styles.nrContainer}>
      <ModuleAccessTypes
        selectedProjectDetails={selectedProjectDetails}
        selectedPaBtn={selectedPaBtn}
        setSelectedPaBtn={setSelectedPaBtn}
        moduleTypes={moduleTypes}
      />

      <Box sx={styles.nrMainContainer}>
        <ProjectDetails
          selectedProjectDetails={selectedProjectDetails}
          projectStatus={projectStatus}
        />

        <VariantDetailsBox selectedVariantDetails={selectedVariantDetails} />

        <SelectModules
          selectedProjectDetails={selectedProjectDetails}
          selectedPaBtn={selectedPaBtn}
          moduleTypes={moduleTypes}
          variantOptionsList={variantOptionsList}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
          zoneOptionsList={zoneOptionsList}
          selectedZone={selectedZone}
          setSelectedZone={setSelectedZone}
          eeOptionsList={eeOptionsList}
          selectedEE={selectedEE}
          setSelectedEE={setSelectedEE}
          swmOptionsList={swmOptionsList}
          selectedSWM={selectedSWM}
          setSelectedSWM={setSelectedSWM}
          vehicleSystemOptionsList={vehicleSystemOptionsList}
          selectedVehicleSystem={selectedVehicleSystem}
          setSelectedVehicleSystem={setSelectedVehicleSystem}
          vehicleComponentOptionsList={vehicleComponentOptionsList}
          selectedVehicleComponent={selectedVehicleComponent}
          setSelectedVehicleComponent={setSelectedVehicleComponent}
        />

        {selectedPaBtn === moduleTypes?.EE && selectedEE && (
          <EEDetails selectedEEDetails={selectedEEDetails} />
        )}

        {selectedPaBtn === moduleTypes?.SWM && selectedSWM && (
          <SWMDetails selectedSWMDetails={selectedSWMDetails} />
        )}

        {selectedPaBtn === moduleTypes?.VEHICLE_SYSTEM &&
          selectedVehicleSystem && (
            <VehicleSystemDetails
              selectedVehicleSystemDetails={selectedVehicleSystemDetails}
            />
          )}

        {selectedPaBtn === moduleTypes?.VEHICLE_COMPONENT &&
          selectedVehicleComponent && (
            <VehicleComponentDetails
              selectedVehicleComponentDetails={selectedVehicleComponentDetails}
            />
          )}

        <Box sx={styles.acATContainer}>
          <Box sx={styles.acATbContainer}>
            <CustomButtonV2
              size={"small"}
              disabled={
                (selectedPaBtn === moduleTypes?.PROJECT &&
                  !selectedProjectDetails?.project_id) ||
                (selectedPaBtn === moduleTypes?.VARIANT &&
                  (!selectedProjectDetails?.project_id || !selectedVariant)) ||
                (selectedPaBtn === moduleTypes?.ZONE &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedZone)) ||
                (selectedPaBtn === moduleTypes?.EE &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedEEDetails?.ee_id)) ||
                (selectedPaBtn === moduleTypes?.SWM &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedEEDetails?.ee_id ||
                    !selectedSWMDetails?.swm_id)) ||
                (selectedPaBtn === moduleTypes?.VEHICLE_SYSTEM &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedVehicleSystem)) ||
                (selectedPaBtn === moduleTypes?.VEHICLE_COMPONENT &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedVehicleSystem ||
                    !selectedVehicleComponent))
              }
              btnName={"Access Details"}
              handleOnClick={() =>
                handleAccessBtnClick({
                  access_type: selectedPaBtn,
                  project_id: selectedProjectDetails?.project_id,
                  variant_id: selectedVariantDetails?.variant_id,
                  zone_position: selectedZoneDetails?.zone?.position,
                  ee_id: selectedEEDetails?.ee_id,
                  swm_id: selectedSWMDetails?.swm_id,
                  vs_id: selectedVehicleSystemDetails?.id,
                  vc_id: selectedVehicleComponentDetails?.id,
                })
              }
            />
          </Box>
        </Box>
      </Box>

      <AccessOverviewDrawer
        open={showOverview}
        data={accessControlData}
        handleClose={() => setShowOverview(false)}
        onManageAccess={() => {
          console.log("OnManageAccess");
          setShowOverview(false);
        }}
      />

    </Box>
  );
}

export default AccessControlTab;

AccessControlTab.propTypes = {
  selectedProjectDetails: PropTypes.any,
  projectStatus: PropTypes.any,
  moduleTypes: PropTypes.any,
  accessRequestStatus: PropTypes.any,
};

const styles = {
  nrContainer: {
    width: "100%",
    display: "flex",
    p: 2,
    mt: 3,
  },
  nrMainContainer: {
    width: "100%",
    border: "1px solid grey",
    ml: 3,
    px: 2,
    pt: 0.6,
    pb: 2,
  },
  acATContainer: {
    mt: 4,
    mb: 0.4,
  },
  acATbContainer: {},
};
